const STORAGE_KEY = "msb_external_recipes_cache";
const MAX_CACHED_RECIPES = 200;

const memoryCache = new Map();

function isBrowserStorageAvailable() {
  if (typeof window === "undefined" || !window.sessionStorage) return false;
  try {
    const testKey = "__msb_storage_test__";
    window.sessionStorage.setItem(testKey, "1");
    window.sessionStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}

function readFromSessionStorage() {
  if (!isBrowserStorageAvailable()) return [];
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function persistToSessionStorage() {
  if (!isBrowserStorageAvailable()) return;
  try {
    const all = Array.from(memoryCache.values());
    const limited = all.slice(-MAX_CACHED_RECIPES);
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(limited));
  } catch {
    // Gracefully handle storage quota or private mode limitations
  }
}

function initMemoryCache() {
  const stored = readFromSessionStorage();
  for (const item of stored) {
    if (item && (item.id != null || item.sourceId != null)) {
      const id = String(item.id ?? item.sourceId);
      memoryCache.set(id, item);
    }
  }
}

initMemoryCache();

export function normalizeRecipe(item) {
  if (!item || typeof item !== "object") return null;

  const snapshot =
    item.recipe && typeof item.recipe === "object" ? item.recipe : {};

  const id = String(item.id ?? item.sourceId ?? snapshot.sourceId ?? "");
  const title = String(item.title ?? item.name ?? snapshot.name ?? "").trim();

  const ingredients =
    Array.isArray(item.ingredients) && item.ingredients.length > 0
      ? item.ingredients
      : Array.isArray(snapshot.ingredients)
        ? snapshot.ingredients
        : [];

  const steps =
    Array.isArray(item.steps) && item.steps.length > 0
      ? item.steps
      : Array.isArray(snapshot.steps)
        ? snapshot.steps
        : [];

  const nutrients = item.nutrients ?? snapshot.nutrients ?? {};
  const kcal = item.kcal ?? snapshot.kcal ?? null;
  const servings = item.servings ?? snapshot.servings ?? null;
  const readyInMinutes = item.readyInMinutes ?? snapshot.readyInMinutes ?? null;

  const foodType = item.foodType ?? snapshot.foodType ?? null;
  const difficulty = item.difficulty ?? snapshot.difficulty ?? null;
  const macrosBase = item.macrosBase ?? snapshot.macrosBase ?? "serving";
  const summary = item.summary ?? snapshot.summary ?? null;
  const sourceUrl = item.sourceUrl ?? snapshot.sourceUrl ?? null;
  const spoonacularSourceUrl =
    item.spoonacularSourceUrl ?? snapshot.spoonacularSourceUrl ?? null;

  const cuisines = item.cuisines ?? snapshot.cuisines ?? [];
  const dishTypes = item.dishTypes ?? snapshot.dishTypes ?? [];
  const diets = item.diets ?? snapshot.diets ?? [];

  const vegetarian = Boolean(item.vegetarian ?? snapshot.vegetarian);
  const vegan = Boolean(item.vegan ?? snapshot.vegan);
  const glutenFree = Boolean(item.glutenFree ?? snapshot.glutenFree);
  const dairyFree = Boolean(item.dairyFree ?? snapshot.dairyFree);

  return {
    ...snapshot,
    ...item,
    id: id ? (Number.isFinite(Number(id)) ? Number(id) : id) : item.id,
    sourceId: id,
    title: title || item.title || snapshot.name || "",
    name: title || item.title || snapshot.name || "",
    image: item.image ?? snapshot.image ?? null,
    imageType: item.imageType ?? snapshot.imageType ?? null,
    kcal,
    nutrients,
    servings,
    readyInMinutes,
    vegetarian,
    vegan,
    glutenFree,
    dairyFree,
    cuisines,
    dishTypes,
    diets,
    foodType,
    difficulty,
    macrosBase,
    summary,
    sourceUrl,
    spoonacularSourceUrl,
    ingredients,
    steps,
    recipe: {
      source: "spoonacular",
      sourceId: id,
      name: title || item.title || snapshot.name || "",
      servings: Number(servings) > 0 ? Number(servings) : 1,
      macrosBase: macrosBase || "serving",
      nutrients,
      kcal,
      ingredients,
      steps,
      image: item.image ?? snapshot.image ?? null,
      foodType,
      difficulty,
      ...snapshot,
    },
  };
}

export function storeRecipes(recipes) {
  if (!recipes) return;
  const list = Array.isArray(recipes) ? recipes : [recipes];
  let changed = false;

  for (const entry of list) {
    if (!entry) continue;
    const normalized = normalizeRecipe(entry);
    if (!normalized) continue;
    const key = String(normalized.id ?? normalized.sourceId);
    if (key) {
      memoryCache.set(key, normalized);
      changed = true;
    }
  }

  if (changed) {
    persistToSessionStorage();
  }
}

export function getRecipe(id) {
  if (id == null) return null;
  const key = String(id);
  const existing = memoryCache.get(key);
  if (existing) return existing;

  const stored = readFromSessionStorage();
  for (const item of stored) {
    if (item && String(item.id ?? item.sourceId) === key) {
      const normalized = normalizeRecipe(item);
      if (normalized) {
        memoryCache.set(key, normalized);
        return normalized;
      }
    }
  }

  return null;
}

export function hasRecipe(id) {
  return Boolean(getRecipe(id));
}

export function getRecipesMap() {
  const map = {};
  for (const [key, value] of memoryCache.entries()) {
    map[key] = value;
  }
  return map;
}

export function clearRecipeCache() {
  memoryCache.clear();
  if (isBrowserStorageAvailable()) {
    try {
      window.sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // Ignore
    }
  }
}
